import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  useSetProductLawApplicability,
  type ProductLawApplicability,
  type ProductLawKey,
} from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

type V2LawApplicabilityOverrideDialogProps = {
  productId: string
  law: ProductLawKey
  lawLabel: string
  applicability: ProductLawApplicability | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function buildForcedApplicability(
  status: 'APPLICABLE' | 'NOT_APPLICABLE',
  forceReason: string
): ProductLawApplicability {
  return {
    status,
    answers: null,
    version: null,
    source: 'USER_FORCE',
    forceReason,
  }
}

export function V2LawApplicabilityOverrideDialog({
  productId,
  law,
  lawLabel,
  applicability,
  open,
  onOpenChange,
}: V2LawApplicabilityOverrideDialogProps) {
  const { t } = useTranslation('common')
  const { mutateAsync: setApplicability, isPending } =
    useSetProductLawApplicability(productId)
  const [reason, setReason] = useState('')
  const [showReasonRequired, setShowReasonRequired] = useState(false)

  useEffect(() => {
    if (!open) {
      return
    }
    setReason(applicability?.forceReason?.trim() ?? '')
    setShowReasonRequired(false)
  }, [open, applicability?.forceReason])

  const trimmedReason = reason.trim()
  const reasonIsValid = trimmedReason.length > 0

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setShowReasonRequired(false)
    }
    onOpenChange(nextOpen)
  }

  async function submitForcedStatus(status: 'APPLICABLE' | 'NOT_APPLICABLE') {
    if (!reasonIsValid) {
      setShowReasonRequired(true)
      return
    }

    try {
      await setApplicability({
        law,
        applicability: buildForcedApplicability(status, trimmedReason),
      })
      handleOpenChange(false)
    } catch {
      toast.error(t('v2.applicability.override.errors.submitFailed'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('v2.applicability.override.dialog.title', { law: lawLabel })}
          </DialogTitle>
          <DialogDescription>
            {law === 'ped'
              ? t('v2.applicability.override.dialog.descriptionPed')
              : t('v2.applicability.override.dialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor={`${law}-override-reason`}>
            {t('v2.applicability.override.dialog.reasonLabel')}
          </Label>
          <Textarea
            id={`${law}-override-reason`}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value)
              if (event.target.value.trim().length > 0) {
                setShowReasonRequired(false)
              }
            }}
            placeholder={t(
              'v2.applicability.override.dialog.reasonPlaceholder'
            )}
            rows={4}
          />
          {showReasonRequired && !reasonIsValid && (
            <p className="text-sm text-destructive">
              {t('v2.applicability.override.dialog.reasonRequired')}
            </p>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col sm:items-stretch">
          <Button
            type="button"
            disabled={isPending}
            onClick={() => {
              void submitForcedStatus('APPLICABLE')
            }}
          >
            {law === 'ped'
              ? t('v2.applicability.override.dialog.forceInScope')
              : t('v2.applicability.override.dialog.forceApplicable')}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isPending}
            onClick={() => {
              void submitForcedStatus('NOT_APPLICABLE')
            }}
          >
            {law === 'ped'
              ? t('v2.applicability.override.dialog.forceNotApplicablePed')
              : t('v2.applicability.override.dialog.forceNotApplicable')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
